import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
    InstructionsWithFieldsContainerComponent,
    InstructionsComponent,
    FormRowComponent,
    TypeaheadSearchComponent,
} from '@r1-compass-library/components';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { DataMartField } from '@r1-compass-library/models';
import { DivisionBasicSettingsFormService } from '../division-basic-settings-form.service';

@Component({
    selector: 'r1-compass-app-multiple-recommendations',
    template: `
        <r1-compass-lib-instructions-with-fields-container>
            <r1-compass-lib-instructions instructions [headerText]="'Multiple Recommendations'">
                Optionally, enable multiple recommendations for all plays in the division. If enabled, select the
                maximum recommendations and field for ranking. More information on multiple recommendations can be found
                in the User Guide.
            </r1-compass-lib-instructions>
            <form formArea [formGroup]="multipleRecommendationsForm">
                <r1-compass-lib-form-row name="Enable Multiple Recommendations" id="enable">
                    <mat-slide-toggle formControlName="enabled" />
                </r1-compass-lib-form-row>
                @if (showFormFields()) {
                    <r1-compass-lib-form-row name="Max allowable recommendations *">
                        <mat-form-field>
                            <mat-label>Max allowable recommendations</mat-label>
                            <input matInput type="number" formControlName="maxRecommendations" min="1" step="1" />
                            @if (multipleRecommendationsForm.controls.maxRecommendations.hasError('pattern')) {
                                <mat-error>Whole numbers only</mat-error>
                            }
                            @if (multipleRecommendationsForm.controls.maxRecommendations.hasError('required')) {
                                <mat-error>Value required.</mat-error>
                            }
                        </mat-form-field>
                    </r1-compass-lib-form-row>
                    <r1-compass-lib-form-row name="Rank Field *">
                        <r1-compass-lib-typeahead-search
                            [valueFormControl]="multipleRecommendationsForm.controls.rankingField"
                            [displayFormControl]="rankFieldOptionDisplayControl"
                            [resultDisplayPropertyPath]="'displayName'"
                            [resultValuePropertyPath]="'name'"
                            [disabled]="false"
                            [searchResults]="rankFieldOptionResults()"
                            (search)="rankFieldOptionsSearchState.set($event)"
                            (resultSelection)="rankFieldOptionsSearchState.set('')" />
                    </r1-compass-lib-form-row>
                }
            </form>
        </r1-compass-lib-instructions-with-fields-container>
    `,
    imports: [
        InstructionsWithFieldsContainerComponent,
        InstructionsComponent,
        ReactiveFormsModule,
        FormRowComponent,
        MatSlideToggleModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        TypeaheadSearchComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        #enable {
            display: block;
            margin-bottom: 1rem;
        }
    `,
})
export class MultipleRecommendationsComponent {
    protected readonly formService = inject(DivisionBasicSettingsFormService);

    protected multipleRecommendationsForm = this.formService.divisionBasicSettingsForm.controls.multipleRecommendations;

    protected rankFieldOptionResults = computed<DataMartField[]>(() => {
        const datamartFields = this.formState.dataMartFields();
        const searchState = this.rankFieldOptionsSearchState();

        return searchState === ''
            ? datamartFields
            : datamartFields.filter(field => field.displayName.toLowerCase().includes(searchState));
    });

    protected rankFieldOptionDisplayControl = new FormControl('');

    protected rankFieldOptionsSearchState = signal('');

    protected showFormFields = computed(() => this.formState().value.multipleRecommendations.enabled);

    private formState = this.formService.divisionBasicSettingsFormState;

    constructor() {
        effect(
            () => {
                const formState = this.formState;

                const rankFieldOptions = formState.dataMartFields();
                const multipleReccsRankingField = formState().value.multipleRecommendations.rankingField;

                rankFieldOptions?.find(datamartFieldOption => {
                    if (datamartFieldOption.name === multipleReccsRankingField) {
                        this.rankFieldOptionDisplayControl.setValue(datamartFieldOption.displayName);
                        this.rankFieldOptionsSearchState.set(datamartFieldOption.name);
                    }
                });
            },
            { debugName: 'init multi reccs form' }
        );
    }
}
